import { useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Table,
  Text,
} from '@mantine/core'
import {
  IconPlugConnected,
  IconPlus,
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'

import { ArcPageShell } from 'components/ArcPageShell'
import { Layout } from 'components/Layout'
import {
  CurrentUserContextProvider,
  NotificationProvider,
  useCurrentUser,
} from 'context'

import AccountsModal from './components/AccountsModal'
import ConnectionDrawer from './components/ConnectionDrawer'
import NewConnectionModal from './components/NewConnectionModal'
import { useConnections } from './hooks/useAdminConnections'
import type { RosterConnectionInfo } from './types'

const AdminConnectionsPage = () => (
  <CurrentUserContextProvider>
    <NotificationProvider>
      <Layout>
        <AdminConnectionsGate />
      </Layout>
    </NotificationProvider>
  </CurrentUserContextProvider>
)

/** Blocks the screen for everyone without the super_admin flag. */
const AdminConnectionsGate = () => {
  const { currentUser } = useCurrentUser()

  if (!currentUser) {
    return (
      <Group position='center' py='xl'>
        <Loader />
      </Group>
    )
  }

  if (!currentUser.super_admin) return <Navigate to='/error_permission' replace />

  return <AdminConnectionsContent />
}

const AdminConnectionsContent = () => {
  const { t } = useTranslation()
  const { connections, isLoading, isError, refetchConnections } = useConnections()
  const [createOpened, setCreateOpened] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [accountsConnection, setAccountsConnection] = useState<RosterConnectionInfo | null>(null)

  // Prefer the fresh copy from the current list so the drawer reflects the
  // latest sync state without closing/reopening.
  const selectedConnection = selectedId
    ? connections.find(candidate => candidate.id === selectedId) ?? null
    : null

  const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleString() : '—'

  const renderBody = () => {
    if (isLoading) {
      return (
        <Group position='center' py='xl'>
          <Loader />
        </Group>
      )
    }

    if (isError) {
      return (
        <Text role='alert' color='red'>
          {t('admin_connections.load_error')}
        </Text>
      )
    }

    if (connections.length === 0) {
      return <Text color='dimmed'>{t('admin_connections.empty')}</Text>
    }

    return (
      <Card withBorder p={0}>
        <Table striped highlightOnHover verticalSpacing='sm'>
          <thead>
            <tr>
              <th>{t('admin_connections.district')}</th>
              <th>{t('admin_connections.sync_status')}</th>
              <th>{t('admin_connections.accounts')}</th>
              <th>{t('admin_connections.last_synced')}</th>
              <th aria-label={t('admin_connections.actions') as string} />
            </tr>
          </thead>
          <tbody>
            {connections.map(connection => (
              <tr key={connection.id}>
                <td>
                  <Text weight={500}>
                    {connection.districtName || t('admin_connections.unnamed_district')}
                  </Text>
                  <Text size='xs' color='dimmed'>
                    {connection.districtExternalId}
                  </Text>
                </td>
                <td>
                  <Badge color={connection.enabled ? 'green' : 'gray'}>
                    {connection.enabled
                      ? t('admin_connections.enabled')
                      : t('admin_connections.disabled')}
                  </Badge>
                </td>
                <td>
                  <Button
                    variant='subtle'
                    size='xs'
                    compact
                    disabled={connection.accountsCount === 0}
                    onClick={() => setAccountsConnection(connection)}
                  >
                    {connection.accountsCount}
                  </Button>
                </td>
                <td>
                  <Text size='sm' color='dimmed'>
                    {formatDate(connection.lastSyncedAt)}
                  </Text>
                </td>
                <td>
                  <Group position='right' noWrap>
                    <Button
                      variant='light'
                      size='xs'
                      leftIcon={<IconPlugConnected size={16} />}
                      onClick={() => setSelectedId(connection.id)}
                    >
                      {t('admin_connections.manage')}
                    </Button>
                  </Group>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    )
  }

  return (
    <ArcPageShell
      title={t('admin_connections.title')}
      subtitle={t('admin_connections.subtitle')}
    >
      <Stack spacing='md'>
        <Group position='right'>
          <Button leftIcon={<IconPlus size={16} />} onClick={() => setCreateOpened(true)}>
            {t('admin_connections.new_connection')}
          </Button>
        </Group>
        {renderBody()}
      </Stack>

      <NewConnectionModal
        opened={createOpened}
        onClose={() => setCreateOpened(false)}
        onCreated={() => {
          setCreateOpened(false)
          refetchConnections()
        }}
      />

      <ConnectionDrawer
        opened={selectedConnection !== null}
        connection={selectedConnection}
        onClose={() => setSelectedId(null)}
        onChanged={refetchConnections}
      />

      <AccountsModal
        connection={accountsConnection}
        onClose={() => setAccountsConnection(null)}
      />
    </ArcPageShell>
  )
}

export default AdminConnectionsPage
